import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { supabase } from "../supabase";
import EmptyState from "../components/EmptyState";

const BOT_TOKEN = "8628392776:AAEIkaTzeyZNhWz7IY0ZCzrNGPs-hJcsweg";

const inputStyle = {
  width: "100%",
  border: "0.5px solid #e0e0e0",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  color: "#111",
  fontFamily: "inherit",
};

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingApplicants, setLoadingApplicants] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("drivers");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingApplicant, setUpdatingApplicant] = useState("");
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    corp_name: "",
    cdl_expiry: "",
    medical_expiry: "",
    chat_id: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDrivers();
    fetchApplicants();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchDrivers = async () => {
    const { data } = await supabase
      .from("drivers")
      .select("*")
      .not("status", "eq", "applied")
      .not("status", "eq", "declined")
      .order("submitted_at", { ascending: false });
    setDrivers(data || []);
    setLoading(false);
  };

  const fetchApplicants = async () => {
    setLoadingApplicants(true);
    const { data } = await supabase
      .from("drivers")
      .select("*")
      .eq("status", "applied")
      .order("submitted_at", { ascending: false });
    setApplicants(data || []);
    setLoadingApplicants(false);
  };

  const sendTelegram = async (chatId, text) => {
    try {
      await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chat_id: chatId, text, parse_mode: "Markdown" }),
      });
    } catch (e) {
      console.error("Telegram error:", e);
    }
  };

  const updateApplicantCheck = async (
    applicantId,
    field,
    value,
    chatId,
    firstName
  ) => {
    setUpdatingApplicant(`${applicantId}-${field}`);
    await supabase
      .from("drivers")
      .update({ [field]: value })
      .eq("id", applicantId);
    const messages = {
      drug_test: {
        pass: `✅ *Hi ${firstName}!*\n\nYour drug test came back *PASSED*. 🎉\n\nWe will now proceed with the next step of your application.\n\n_Porso Freight Team 🚛_`,
        fail: `❌ *Hi ${firstName},*\n\nUnfortunately your drug test did not meet our requirements.\n\nThank you for your interest in Porso Freight.\n\n_Porso Freight Team 🚛_`,
      },
      cdl_history: {
        pass: `✅ *Hi ${firstName}!*\n\nYour CDL history check came back *PASSED*. 🎉\n\nWe will now proceed with the next step of your application.\n\n_Porso Freight Team 🚛_`,
        fail: `❌ *Hi ${firstName},*\n\nUnfortunately your CDL history check did not meet our requirements.\n\nThank you for your interest in Porso Freight.\n\n_Porso Freight Team 🚛_`,
      },
    };
    if (chatId && messages[field]?.[value]) {
      await sendTelegram(chatId, messages[field][value]);
    }
    setApplicants((prev) =>
      prev.map((a) => (a.id === applicantId ? { ...a, [field]: value } : a))
    );
    setUpdatingApplicant("");
  };

  const approveApplicant = async (applicant) => {
    setUpdatingApplicant(`${applicant.id}-approve`);
    await supabase
      .from("drivers")
      .update({ status: "approved" })
      .eq("id", applicant.id);
    if (applicant.chat_id) {
      await sendTelegram(
        applicant.chat_id,
        `🎉 *Great news, ${applicant.first_name}!*\n\nYou have passed all requirements and have been approved to join the Porso Freight team. Welcome aboard!\n\nTo complete your onboarding, please type /continue in this chat.\n\n_Porso Freight Team 🚛_`
      );
    }
    setApplicants((prev) => prev.filter((a) => a.id !== applicant.id));
    fetchDrivers();
    setUpdatingApplicant("");
  };

  const declineApplicant = async (applicant) => {
    setUpdatingApplicant(`${applicant.id}-decline`);
    await supabase
      .from("drivers")
      .update({ status: "declined" })
      .eq("id", applicant.id);
    if (applicant.chat_id) {
      await sendTelegram(
        applicant.chat_id,
        `*Hi ${applicant.first_name},*\n\nUnfortunately, after reviewing your application, we are unable to move forward at this time. Thank you for your interest in Porso Freight, and we wish you the best.\n\n_Porso Freight Team 🚛_`
      );
    }
    setApplicants((prev) => prev.filter((a) => a.id !== applicant.id));
    setUpdatingApplicant("");
  };

  const getStatus = (driver) => {
    if (driver.drug_test === "fail" || driver.cdl_history === "fail")
      return "failed";
    if (driver.drug_test === "pass" && driver.cdl_history === "pass")
      return "passed";
    return "pending";
  };

  const filteredDrivers = drivers.filter((driver) => {
    const matchesSearch =
      driver.first_name?.toLowerCase().includes(search.toLowerCase()) ||
      driver.last_name?.toLowerCase().includes(search.toLowerCase()) ||
      driver.email?.toLowerCase().includes(search.toLowerCase()) ||
      driver.phone?.includes(search);
    const status = getStatus(driver);
    const matchesFilter = filter === "all" || status === filter;
    return matchesSearch && matchesFilter;
  });

  const getBadgeStyle = (status) => {
    const map = {
      pass: { background: "#eaf3de", color: "#3b6d11" },
      fail: { background: "#fcebeb", color: "#a32d2d" },
      pending: { background: "#faeeda", color: "#854f0b" },
      sent: { background: "#e6f1fb", color: "#185fa5" },
    };
    return map[status] || map.pending;
  };

  const getInitials = (first, last) => `${first?.[0] || ""}${last?.[0] || ""}`;

  const resetForm = () =>
    setForm({
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      corp_name: "",
      cdl_expiry: "",
      medical_expiry: "",
      chat_id: "",
    });

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      alert("First name and last name are required");
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("drivers").insert({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      corp_name: form.corp_name.trim() || null,
      cdl_expiry: form.cdl_expiry || null,
      med_card_expiry: form.medical_expiry || null,
      chat_id: form.chat_id.trim() ? parseInt(form.chat_id.trim()) : null,
      drug_test: "pending",
      cdl_history: "pending",
      contract_sent: "pending",
      status: "active",
      submitted_at: new Date().toISOString(),
    });
    if (error) {
      alert("Error adding driver: " + error.message);
    } else {
      setShowAddModal(false);
      resetForm();
      fetchDrivers();
    }
    setSaving(false);
  };

  const filterButtons = [
    { label: "All", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "Passed", value: "passed" },
    { label: "Failed", value: "failed" },
  ];

  const fieldInput = (label, key, type = "text", placeholder = "") => (
    <div>
      <div
        style={{
          fontSize: "11px",
          fontWeight: "500",
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: "4px",
        }}
      >
        {label}
      </div>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
        placeholder={placeholder}
        style={inputStyle}
      />
    </div>
  );

  const canApprove = (applicant) =>
    applicant.drug_test === "pass" && applicant.cdl_history === "pass";

  const tabStyle = (active) => ({
    padding: "10px 16px",
    fontSize: "13px",
    fontWeight: "500",
    color: active ? "#3a9e5f" : "#6b7280",
    border: "none",
    background: "none",
    cursor: "pointer",
    borderBottom: active ? "2px solid #3a9e5f" : "2px solid transparent",
    marginBottom: "-0.5px",
    fontFamily: "inherit",
    transition: "all 0.15s",
  });

  const checkBtn = (active, type) => ({
    background: active
      ? type === "pass"
        ? "#3b6d11"
        : "#a32d2d"
      : type === "pass"
      ? "#eaf3de"
      : "#fcebeb",
    color: active ? "#fff" : type === "pass" ? "#3b6d11" : "#a32d2d",
    border: `0.5px solid ${type === "pass" ? "#c0dd97" : "#f7c1c1"}`,
    borderRadius: "6px",
    padding: "5px 10px",
    fontSize: "11px",
    fontWeight: "500",
    cursor: active ? "not-allowed" : "pointer",
    fontFamily: "inherit",
  });

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f8f8f8" }}>
      {!isMobile && <Sidebar />}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isMobile && <MobileHeader title="Drivers" />}

        {/* Top bar */}
        <div
          style={{
            background: "#fff",
            borderBottom: "0.5px solid #e8e8e8",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <div className="t-page-title">Drivers</div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              placeholder={
                isMobile ? "Search..." : "Search by name, email or phone..."
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                fontSize: "13px",
                border: "0.5px solid #e0e0e0",
                borderRadius: "8px",
                padding: "7px 12px",
                width: isMobile ? "120px" : "240px",
                outline: "none",
                color: "#111",
              }}
            />
            <button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              style={{
                background: "#1a1e1c",
                color: "#fff",
                fontSize: "13px",
                fontWeight: "500",
                padding: "7px 14px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                whiteSpace: "nowrap",
                fontFamily: "inherit",
              }}
            >
              + Add driver
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            background: "#fff",
            borderBottom: "0.5px solid #e8e8e8",
            padding: "0 16px",
            display: "flex",
          }}
        >
          <button
            onClick={() => setTab("drivers")}
            style={tabStyle(tab === "drivers")}
          >
            Drivers
            <span
              style={{
                marginLeft: "6px",
                fontSize: "11px",
                background: "#eaf3de",
                color: "#3b6d11",
                padding: "1px 6px",
                borderRadius: "10px",
              }}
            >
              {drivers.length}
            </span>
          </button>
          <button
            onClick={() => setTab("applicants")}
            style={tabStyle(tab === "applicants")}
          >
            Applicants
            {applicants.length > 0 && (
              <span
                style={{
                  marginLeft: "6px",
                  fontSize: "11px",
                  background: "#e6f1fb",
                  color: "#185fa5",
                  padding: "1px 6px",
                  borderRadius: "10px",
                }}
              >
                {applicants.length}
              </span>
            )}
          </button>
        </div>

        <div style={{ padding: isMobile ? "12px" : "20px 24px" }}>
          {/* ── DRIVERS TAB ── */}
          {tab === "drivers" && (
            <>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginBottom: "16px",
                  flexWrap: "wrap",
                }}
              >
                {filterButtons.map((btn) => (
                  <button
                    key={btn.value}
                    onClick={() => setFilter(btn.value)}
                    style={{
                      padding: "5px 14px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "500",
                      letterSpacing: "-0.01em",
                      border: "0.5px solid",
                      cursor: "pointer",
                      transition: "all 0.15s",
                      background: filter === btn.value ? "#3a9e5f" : "#fff",
                      color: filter === btn.value ? "#fff" : "#666",
                      borderColor: filter === btn.value ? "#3a9e5f" : "#e0e0e0",
                    }}
                  >
                    {btn.label}
                    <span
                      style={{
                        marginLeft: "6px",
                        fontSize: "11px",
                        opacity: 0.75,
                      }}
                    >
                      {btn.value === "all"
                        ? drivers.length
                        : drivers.filter((d) => getStatus(d) === btn.value)
                            .length}
                    </span>
                  </button>
                ))}
              </div>

              <div
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                {!isMobile && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr 0.5fr",
                      padding: "10px 16px",
                      background: "#fafafa",
                      borderBottom: "0.5px solid #e8e8e8",
                    }}
                  >
                    {[
                      "Driver",
                      "Contact",
                      "Drug test",
                      "CDL history",
                      "Contract",
                      "",
                    ].map((h) => (
                      <div key={h} className="t-table-header">
                        {h}
                      </div>
                    ))}
                  </div>
                )}
                {loading ? (
                  <div
                    style={{ padding: "40px", textAlign: "center" }}
                    className="t-body"
                  >
                    Loading...
                  </div>
                ) : filteredDrivers.length === 0 ? (
                  drivers.length === 0 ? (
                    <EmptyState
                      title="No Records Found"
                      subtitle="Drivers will appear here once they complete onboarding."
                      ctaLabel="+ Add driver"
                      onCta={() => {
                        resetForm();
                        setShowAddModal(true);
                      }}
                    />
                  ) : (
                    <EmptyState
                      title="No Records Found"
                      subtitle="Try adjusting your search or filters."
                    />
                  )
                ) : (
                  filteredDrivers.map((driver) =>
                    isMobile ? (
                      <div
                        key={driver.id}
                        onClick={() => navigate(`/drivers/${driver.id}`)}
                        style={{
                          padding: "12px 16px",
                          borderBottom: "0.5px solid #f0f0f0",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                background: "#eaf3de",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                fontWeight: "500",
                                color: "#3a9e5f",
                                flexShrink: 0,
                              }}
                            >
                              {getInitials(driver.first_name, driver.last_name)}
                            </div>
                            <div>
                              <div className="t-name">
                                {driver.first_name} {driver.last_name}
                              </div>
                              <div
                                className="t-data"
                                style={{ color: "#9ca3af" }}
                              >
                                {driver.phone}
                              </div>
                            </div>
                          </div>
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#3a9e5f",
                              fontWeight: "500",
                            }}
                          >
                            View →
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            flexWrap: "wrap",
                          }}
                        >
                          {[
                            driver.drug_test,
                            driver.cdl_history,
                            driver.contract_sent,
                          ].map((s, i) => (
                            <span
                              key={i}
                              className="t-badge"
                              style={{
                                padding: "2px 8px",
                                borderRadius: "20px",
                                ...getBadgeStyle(s),
                              }}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div
                        key={driver.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr 0.5fr",
                          padding: "12px 16px",
                          borderBottom: "0.5px solid #f0f0f0",
                          alignItems: "center",
                          cursor: "pointer",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#fafafa")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "#fff")
                        }
                        onClick={() => navigate(`/drivers/${driver.id}`)}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <div
                            style={{
                              width: "30px",
                              height: "30px",
                              borderRadius: "50%",
                              background: driver.chat_id
                                ? "#eaf3de"
                                : "#f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11px",
                              fontWeight: "500",
                              color: driver.chat_id ? "#3a9e5f" : "#9ca3af",
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(driver.first_name, driver.last_name)}
                          </div>
                          <div>
                            <div className="t-name">
                              {driver.first_name} {driver.last_name}
                            </div>
                            <div
                              className="t-data"
                              style={{ color: "#9ca3af" }}
                            >
                              {new Date(
                                driver.submitted_at
                              ).toLocaleDateString()}
                              {!driver.chat_id && (
                                <span
                                  style={{
                                    marginLeft: "6px",
                                    fontSize: "10px",
                                    color: "#854f0b",
                                    background: "#faeeda",
                                    padding: "1px 6px",
                                    borderRadius: "10px",
                                  }}
                                >
                                  No Telegram
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="t-data" style={{ color: "#374151" }}>
                            {driver.phone}
                          </div>
                          <div className="t-data" style={{ color: "#9ca3af" }}>
                            {driver.email}
                          </div>
                        </div>
                        <div>
                          <span
                            className="t-badge"
                            style={{
                              padding: "3px 8px",
                              borderRadius: "20px",
                              ...getBadgeStyle(driver.drug_test),
                            }}
                          >
                            {driver.drug_test}
                          </span>
                        </div>
                        <div>
                          <span
                            className="t-badge"
                            style={{
                              padding: "3px 8px",
                              borderRadius: "20px",
                              ...getBadgeStyle(driver.cdl_history),
                            }}
                          >
                            {driver.cdl_history}
                          </span>
                        </div>
                        <div>
                          <span
                            className="t-badge"
                            style={{
                              padding: "3px 8px",
                              borderRadius: "20px",
                              ...getBadgeStyle(driver.contract_sent),
                            }}
                          >
                            {driver.contract_sent}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#3a9e5f",
                            fontWeight: "500",
                          }}
                        >
                          View →
                        </div>
                      </div>
                    )
                  )
                )}
              </div>
              <div
                className="t-label"
                style={{ marginTop: "10px", color: "#9ca3af" }}
              >
                {filteredDrivers.length} driver
                {filteredDrivers.length !== 1 ? "s" : ""}{" "}
                {filter !== "all" ? `(${filter})` : "total"}
              </div>
            </>
          )}

          {/* ── APPLICANTS TAB ── */}
          {tab === "applicants" && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {loadingApplicants ? (
                <div
                  style={{ padding: "40px", textAlign: "center" }}
                  className="t-body"
                >
                  Loading applicants...
                </div>
              ) : applicants.length === 0 ? (
                <div
                  style={{
                    background: "#fff",
                    border: "0.5px solid #e8e8e8",
                    borderRadius: "12px",
                    padding: "40px",
                    textAlign: "center",
                  }}
                  className="t-body"
                >
                  No applicants yet — drivers who apply via the bot will appear
                  here
                </div>
              ) : (
                applicants.map((applicant) => {
                  const isUpdating = updatingApplicant.startsWith(applicant.id);
                  const approved = canApprove(applicant);
                  return (
                    <div
                      key={applicant.id}
                      style={{
                        background: "#fff",
                        border: "0.5px solid #e8e8e8",
                        borderRadius: "12px",
                        padding: "16px",
                      }}
                    >
                      {/* Header */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <div
                            style={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "50%",
                              background: "#faeeda",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              fontWeight: "500",
                              color: "#854f0b",
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(
                              applicant.first_name,
                              applicant.last_name
                            )}
                          </div>
                          <div>
                            <div className="t-name">
                              {applicant.first_name} {applicant.last_name}
                            </div>
                            <div
                              className="t-label"
                              style={{ color: "#9ca3af", marginTop: "1px" }}
                            >
                              Applied{" "}
                              {new Date(
                                applicant.applied_at || applicant.submitted_at
                              ).toLocaleDateString()}{" "}
                              · via bot
                              {!applicant.chat_id && (
                                <span
                                  style={{
                                    marginLeft: "6px",
                                    fontSize: "10px",
                                    color: "#854f0b",
                                    background: "#faeeda",
                                    padding: "1px 6px",
                                    borderRadius: "10px",
                                  }}
                                >
                                  No Telegram
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            padding: "3px 10px",
                            borderRadius: "20px",
                            background: "#e6f1fb",
                            color: "#185fa5",
                          }}
                        >
                          applied
                        </span>
                      </div>

                      {/* Docs + checks */}
                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          alignItems: "flex-start",
                          marginBottom: "14px",
                          flexWrap: "wrap",
                        }}
                      >
                        {/* CDL */}
                        <div>
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: "500",
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              marginBottom: "6px",
                            }}
                          >
                            CDL
                          </div>
                          {applicant.cdl_link_sb || applicant.cdl_link ? (
                            <div
                              onClick={() =>
                                setLightboxUrl(
                                  applicant.cdl_link_sb || applicant.cdl_link
                                )
                              }
                              style={{
                                width: "90px",
                                height: "64px",
                                borderRadius: "6px",
                                background: "#f0f0f0",
                                border: "0.5px solid #e8e8e8",
                                overflow: "hidden",
                                cursor: "zoom-in",
                              }}
                            >
                              <img
                                src={
                                  applicant.cdl_link_sb || applicant.cdl_link
                                }
                                alt="CDL"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          ) : (
                            <div
                              style={{
                                width: "90px",
                                height: "64px",
                                borderRadius: "6px",
                                background: "#f0f0f0",
                                border: "0.5px solid #e8e8e8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <div style={{ textAlign: "center" }}>
                                <div style={{ fontSize: "20px" }}>🪪</div>
                                <div
                                  style={{
                                    fontSize: "9px",
                                    color: "#9ca3af",
                                    marginTop: "2px",
                                  }}
                                >
                                  Not uploaded
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Medical card */}
                        <div>
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: "500",
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              marginBottom: "6px",
                            }}
                          >
                            Medical card
                          </div>
                          {applicant.med_card_link_sb ||
                          applicant.med_card_link ? (
                            <div
                              onClick={() =>
                                setLightboxUrl(
                                  applicant.med_card_link_sb ||
                                    applicant.med_card_link
                                )
                              }
                              style={{
                                width: "90px",
                                height: "64px",
                                borderRadius: "6px",
                                background: "#f0f0f0",
                                border: "0.5px solid #e8e8e8",
                                overflow: "hidden",
                                cursor: "zoom-in",
                              }}
                            >
                              <img
                                src={
                                  applicant.med_card_link_sb ||
                                  applicant.med_card_link
                                }
                                alt="Medical Card"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          ) : (
                            <div
                              style={{
                                width: "90px",
                                height: "64px",
                                borderRadius: "6px",
                                background: "#f0f0f0",
                                border: "0.5px solid #e8e8e8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <div style={{ textAlign: "center" }}>
                                <div style={{ fontSize: "20px" }}>🏥</div>
                                <div
                                  style={{
                                    fontSize: "9px",
                                    color: "#9ca3af",
                                    marginTop: "2px",
                                  }}
                                >
                                  Not uploaded
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Checks */}
                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            minWidth: "200px",
                          }}
                        >
                          {[
                            { key: "cdl_history", label: "CDL history" },
                            { key: "drug_test", label: "Drug test" },
                          ].map(({ key, label }) => (
                            <div key={key}>
                              <div
                                style={{
                                  fontSize: "10px",
                                  fontWeight: "500",
                                  color: "#9ca3af",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                  marginBottom: "6px",
                                }}
                              >
                                {label}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <button
                                  onClick={() =>
                                    updateApplicantCheck(
                                      applicant.id,
                                      key,
                                      "pass",
                                      applicant.chat_id,
                                      applicant.first_name
                                    )
                                  }
                                  disabled={
                                    isUpdating || applicant[key] === "pass"
                                  }
                                  style={checkBtn(
                                    applicant[key] === "pass",
                                    "pass"
                                  )}
                                >
                                  ✓ Pass
                                </button>
                                <button
                                  onClick={() =>
                                    updateApplicantCheck(
                                      applicant.id,
                                      key,
                                      "fail",
                                      applicant.chat_id,
                                      applicant.first_name
                                    )
                                  }
                                  disabled={
                                    isUpdating || applicant[key] === "fail"
                                  }
                                  style={checkBtn(
                                    applicant[key] === "fail",
                                    "fail"
                                  )}
                                >
                                  ✕ Fail
                                </button>
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    padding: "2px 8px",
                                    borderRadius: "20px",
                                    ...getBadgeStyle(
                                      applicant[key] || "pending"
                                    ),
                                  }}
                                >
                                  {applicant[key] || "pending"}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Divider */}
                      <div
                        style={{
                          height: "0.5px",
                          background: "#f0f0f0",
                          margin: "12px 0",
                        }}
                      />

                      {/* Actions */}
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <button
                          onClick={() => approveApplicant(applicant)}
                          disabled={!approved || isUpdating}
                          style={{
                            background: approved ? "#3a9e5f" : "#e0e0e0",
                            color: approved ? "#fff" : "#9ca3af",
                            border: "none",
                            borderRadius: "8px",
                            padding: "8px 16px",
                            fontSize: "12px",
                            fontWeight: "500",
                            cursor:
                              approved && !isUpdating
                                ? "pointer"
                                : "not-allowed",
                            fontFamily: "inherit",
                            transition: "all 0.15s",
                          }}
                        >
                          {updatingApplicant === `${applicant.id}-approve`
                            ? "Approving..."
                            : "✓ Approve — send stage 2"}
                        </button>
                        <button
                          onClick={() => declineApplicant(applicant)}
                          disabled={isUpdating}
                          style={{
                            background: "#fcebeb",
                            color: "#a32d2d",
                            border: "none",
                            borderRadius: "8px",
                            padding: "8px 16px",
                            fontSize: "12px",
                            fontWeight: "500",
                            cursor: isUpdating ? "not-allowed" : "pointer",
                            fontFamily: "inherit",
                          }}
                        >
                          {updatingApplicant === `${applicant.id}-decline`
                            ? "Declining..."
                            : "✕ Decline"}
                        </button>
                        {!approved && (
                          <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                            {applicant.drug_test !== "pass" &&
                            applicant.cdl_history !== "pass"
                              ? "Both checks must pass to approve"
                              : applicant.drug_test !== "pass"
                              ? "Drug test still pending"
                              : "CDL history still pending"}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Driver Modal */}
      {showAddModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddModal(false);
              resetForm();
            }
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "520px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "0.5px solid #e8e8e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div className="t-name" style={{ fontSize: "15px" }}>
                  Add driver
                </div>
                <div
                  className="t-label"
                  style={{ color: "#9ca3af", marginTop: "2px" }}
                >
                  Manually add a driver to the platform
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                style={{
                  background: "#f0f0f0",
                  border: "none",
                  borderRadius: "8px",
                  width: "30px",
                  height: "30px",
                  cursor: "pointer",
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#555",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                padding: "20px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "14px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                {fieldInput("First name *", "first_name", "text", "John")}
                {fieldInput("Last name *", "last_name", "text", "Doe")}
              </div>
              {fieldInput(
                "Company / Corp name",
                "corp_name",
                "text",
                "e.g. JD Trucking LLC"
              )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                {fieldInput("Phone", "phone", "tel", "+1 (555) 000-0000")}
                {fieldInput("Email", "email", "email", "john@example.com")}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                {fieldInput("CDL expiry", "cdl_expiry", "date")}
                {fieldInput("Medical card expiry", "medical_expiry", "date")}
              </div>
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "4px",
                  }}
                >
                  Telegram Chat ID{" "}
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#9ca3af",
                      textTransform: "none",
                      fontWeight: "400",
                    }}
                  >
                    (optional)
                  </span>
                </div>
                <input
                  type="text"
                  value={form.chat_id}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, chat_id: e.target.value }))
                  }
                  placeholder="e.g. 198233634"
                  style={inputStyle}
                />
              </div>
            </div>
            <div
              style={{
                padding: "16px 24px",
                borderTop: "0.5px solid #e8e8e8",
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                style={{
                  background: "#f0f0f0",
                  color: "#555",
                  fontSize: "13px",
                  fontWeight: "500",
                  padding: "8px 18px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  background: saving ? "#9ca3af" : "#3a9e5f",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: "500",
                  padding: "8px 18px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: saving ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                {saving ? "Saving..." : "Add driver"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.9)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            cursor: "zoom-out",
          }}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              cursor: "pointer",
              fontSize: "20px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
          <img
            src={lightboxUrl}
            alt="Document preview"
            style={{
              maxWidth: "100%",
              maxHeight: "90vh",
              objectFit: "contain",
              borderRadius: "8px",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
